import { useEffect, useLayoutEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { closeOnOutsideTap } from '../utils/closeOnOutsideTap'
import { copyText } from '../utils/clipboard'
import type { LinkAction, LinkMenuModel } from '../links/linkActions'
import type { IconName } from './Icon'
import { Icon } from './Icon'
import { LinkDisplaySwitch } from './LinkDisplaySwitch'
import { notify } from './Notifications'
import { afterExit } from './sheetMotion'

/** One row of an actions menu: a link's (links/linkActions.ts) or a note's (the note's ⋯). */
export type MenuAction = Omit<LinkAction, 'id' | 'icon'> & { id: string; icon: IconName }
export interface MenuModel { groups: MenuAction[][]; display?: LinkMenuModel['display'] }

interface LinkActionsMenuProps {
  model: MenuModel
  title: string
  /** What the menu is about, for assistive technology ("Link actions", "Note actions"). */
  kind?: string
  /** A quiet line at the end (a note's edited and created times). */
  footer?: string
  /** The kind and source, under the title. */
  meta?: string
  /** What the menu opens from: the ⋯ button, a pill or a row. Pointing at it keeps the menu from closing itself on that tap. */
  anchor: HTMLElement | null
  onClose: () => void
}

/** Copy a link's address and say so: the menu has closed, so the answer is a toast. */
export function copyLinkAddress(href: string): void {
  void copyText(href).then(() => notify.success('Link copied'), () => notify.error('Could not copy the link'))
}

const EDGE = 8
const GAP = 6
const WORDS = { chip: 'Pill', row: 'Row', preview: 'Preview' } as const

interface Place { left: number; top: number; side: 'above' | 'below' }

/**
 * Below the anchor when there is room, above it otherwise; lined up with its right edge when it is a button (the ⋯ sits at the
 * end of a line) and its left edge when it is a pill or a row. Pure geometry: the same anchor and viewport give the same place.
 */
export function placeLinkMenu(
  anchor: { left: number; right: number; top: number; bottom: number },
  size: { width: number; height: number },
  view: { top: number; bottom: number; left: number; right: number },
  align: 'start' | 'end',
): Place {
  const roomBelow = view.bottom - anchor.bottom - GAP - EDGE
  const roomAbove = anchor.top - view.top - GAP - EDGE
  const above = roomBelow < size.height && roomAbove > roomBelow
  const wanted = align === 'end' ? anchor.right - size.width : anchor.left
  const left = Math.max(view.left + EDGE, Math.min(wanted, view.right - size.width - EDGE))
  const rawTop = above ? anchor.top - size.height - GAP : anchor.bottom + GAP
  const top = Math.max(view.top + EDGE, Math.min(rawTop, view.bottom - size.height - EDGE))
  return { left, top, side: above ? 'above' : 'below' }
}

function Item({ action, onPick }: { action: MenuAction; onPick: (run: () => void) => void }) {
  const className = ['link-actions__item', action.id === 'open' && 'is-open', action.danger && 'is-danger'].filter(Boolean).join(' ')
  const content = <><Icon name={action.icon} /><span>{action.label}</span>{action.hint ? <small>{action.hint}</small> : null}</>
  if (action.href) return <a className={className} role="menuitem" href={action.href} target="_blank" rel="noreferrer noopener" onClick={() => onPick(() => undefined)}>{content}</a>
  return <button type="button" className={className} role="menuitem" disabled={action.disabled} onClick={() => onPick(action.run)}>{content}</button>
}

function MenuBody({ model, title, meta, kind = 'Link actions', footer, onPick }: { model: MenuModel; title: string; meta?: string; kind?: string; footer?: string; onPick: (run: () => void) => void }) {
  // Remove always comes last, with Show as just above it.
  const { groups, display } = model
  const last = groups[groups.length - 1]
  const removeLast = last !== undefined && last.every((action) => action.danger)
  const lead = removeLast ? groups.slice(0, -1) : groups
  const group = (actions: readonly MenuAction[], key: string) => <div className="link-actions__group" role="group" key={key}>
    {actions.map((action) => <Item key={action.id} action={action} onPick={onPick} />)}
  </div>
  return <div className="link-actions" role="menu" aria-label={title ? `${kind}: ${title}` : kind}>
    {title ? <div className="link-actions__head"><strong>{title}</strong>{meta ? <span>{meta}</span> : null}</div> : null}
    {lead.map((actions, index) => group(actions, `g${index}`))}
    {display ? <div className="link-actions__show">
      <span>Show as</span>
      <LinkDisplaySwitch value={display.value} onChange={(choice) => onPick(() => display.onChange(choice))} labels={WORDS} showLabels />
    </div> : null}
    {removeLast ? group(last, 'danger') : null}
    {footer ? <p className="link-actions__foot">{footer}</p> : null}
  </div>
}

/**
 * The one list of what can be done with a link (links/linkActions.ts), opened from a pill, a row, a card or an entry on the Links
 * screen, and with a note (its ⋯): a popover beside its anchor on a desktop, an action sheet on a phone.
 */
export function LinkActionsMenu(props: LinkActionsMenuProps) {
  const phone = document.documentElement.dataset.design === 'mobile'
  return phone ? <MenuSheet {...props} /> : <MenuPopover {...props} />
}

function MenuSheet({ model, title, meta, kind = 'Link actions', footer, onClose }: Omit<LinkActionsMenuProps, 'anchor'>) {
  const dialog = useRef<HTMLDialogElement>(null)
  useEffect(() => { dialog.current?.showModal() }, [])
  const pick = (run: () => void) => { dialog.current?.close(); run() }
  return createPortal(
    <dialog ref={dialog} className="studio-sheet--self sheet-panel sheet-menu link-actions-sheet" onClose={() => afterExit(onClose)} onClick={closeOnOutsideTap} aria-label={title ? `${kind}: ${title}` : kind}>
      <MenuBody model={model} title={title} meta={meta} kind={kind} footer={footer} onPick={pick} />
    </dialog>,
    document.body,
  )
}

function MenuPopover({ model, title, meta, kind, footer, anchor, onClose }: LinkActionsMenuProps) {
  const panel = useRef<HTMLDivElement>(null)
  useLayoutEffect(() => {
    const element = panel.current
    if (!element || !anchor) return
    const measure = () => {
      const box = (anchor.closest('[data-note-link-unit]') ?? anchor).getBoundingClientRect()
      const viewport = window.visualViewport
      const top = viewport?.offsetTop ?? 0
      const place = placeLinkMenu(box, { width: element.offsetWidth, height: element.offsetHeight },
        { top, bottom: top + (viewport?.height ?? window.innerHeight), left: 0, right: window.innerWidth }, anchor.tagName === 'BUTTON' ? 'end' : 'start')
      element.style.left = `${place.left}px`
      element.style.top = `${place.top}px`
      element.dataset.side = place.side
    }
    measure()
    window.addEventListener('resize', measure)
    window.visualViewport?.addEventListener('resize', measure)
    return () => { window.removeEventListener('resize', measure); window.visualViewport?.removeEventListener('resize', measure) }
  }, [anchor, model])
  useEffect(() => {
    const down = (event: Event) => {
      const target = event.target as Node
      if (!panel.current?.contains(target) && !anchor?.contains(target)) onClose()
    }
    // Escape closes the menu and nothing else: it must not also leave the note.
    const key = (event: KeyboardEvent) => { if (event.key === 'Escape') { event.stopPropagation(); event.preventDefault(); onClose() } }
    document.addEventListener('pointerdown', down, true)
    document.addEventListener('keydown', key, true)
    // The page scrolling away from the anchor closes it; the list scrolling inside itself does not.
    const scrolled = (event: Event) => { if (!panel.current?.contains(event.target as Node)) onClose() }
    window.addEventListener('scroll', scrolled, true)
    return () => {
      document.removeEventListener('pointerdown', down, true)
      document.removeEventListener('keydown', key, true)
      window.removeEventListener('scroll', scrolled, true)
    }
  }, [anchor, onClose])
  const pick = (run: () => void) => { onClose(); run() }
  return createPortal(
    <div ref={panel} className="studio-dropdown link-actions-popover">
      <MenuBody model={model} title={title} meta={meta} kind={kind} footer={footer} onPick={pick} />
    </div>,
    document.body,
  )
}
